// Adaptive poller for callback status.
//
// Strategy (TS-127 §3.1):
//   - First poll after 1.5s (race window for phone_callback_log INSERT).
//   - Exponential backoff: 1.5s, 2s, 3s, 5s, 5s, … capped at 5s.
//   - Hard stop after 5 minutes regardless of state.
//   - Terminal states (answered/busy/no_answer/failed/offline) stop early.
//
// The callback is invoked once per tick. The returned function cancels the
// next scheduled tick and marks the subscription inactive so any later poll
// result is dropped.

import type { CallbackState, CallbackStatus } from './types'

const TICK_FAST_MS = 1500
const TICK_SLOW_MS = 5000
const MAX_DURATION_MS = 5 * 60 * 1000
const TRANSITIONS_TO_SLOW = 3 // after 3 fast ticks, back off to 5s

const TERMINAL_STATES: ReadonlySet<CallbackState> = new Set([
  'answered',
  'busy',
  'no_answer',
  'failed',
  'offline',
])

export type StatusFetcher = (token: string) => Promise<CallbackStatus>
export type StatusListener = (s: CallbackStatus) => void

interface Subscription {
  cancel: () => void
}

export function subscribeStatus(
  token: string,
  fetcher: StatusFetcher,
  cb: StatusListener,
): () => void {
  let cancelled = false
  let timer: ReturnType<typeof setTimeout> | null = null
  let tickCount = 0
  const startedAt = Date.now()

  const schedule = (fn: () => void, delayMs: number) => {
    if (cancelled) return
    timer = setTimeout(fn, delayMs)
  }

  const poll = async () => {
    if (cancelled) return
    if (Date.now() - startedAt > MAX_DURATION_MS) {
      cancelled = true
      return
    }

    let status: CallbackStatus
    try {
      status = await fetcher(token)
    } catch {
      // Network/server hiccup: try again next tick, don't surface to caller.
      tickCount += 1
      schedule(poll, nextDelay(tickCount))
      return
    }

    if (cancelled) return
    cb(status)
    if (TERMINAL_STATES.has(status.state)) {
      cancelled = true
      return
    }

    tickCount += 1
    schedule(poll, nextDelay(tickCount))
  }

  // Kick off the first poll immediately so the SDK consumer gets an early
  // "still pending" tick within the first 1.5s of submission.
  schedule(poll, TICK_FAST_MS)

  return () => {
    cancelled = true
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }
}

function nextDelay(tickCount: number): number {
  return tickCount <= TRANSITIONS_TO_SLOW ? TICK_FAST_MS : TICK_SLOW_MS
}

// Convenience for tests / inspection. Not part of the public API.
export const _internal = {
  TICK_FAST_MS,
  TICK_SLOW_MS,
  MAX_DURATION_MS,
  TRANSITIONS_TO_SLOW,
  TERMINAL_STATES,
  nextDelay,
}
